import { CTA, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { useFeatureServices } from '../../use-feature-services';
import { Question1 } from './question1';
import { Question2 } from './question2';
import { Question3 } from './question3';
import { QuizFormLoader } from './quizFormLoader';

const BackgroundGtiColor = styled.section`
  width: 100%;
  height: 100dvh;
  position: relative;
  display: flex;
  flex-direction: column;

  background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/textura-negra2?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT0xMDgwJmZtdD13ZWJwJnFsdD03OSZiZmM9b2ZmJmJmMTc=');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  overflow-x: hidden;
`;

const BottomContainer = styled.div`
  position: absolute;
  bottom: 50px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 0 10%;

  @media (max-width: 960px) {
    padding: 0 5%;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  gap: 15px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 960px) {
    position: static;
    transform: none;
  }
`;

const StepNumber = styled.div<{ active: boolean }>`
  color: ${({ active }) => (active ? '#C90010' : '#ffffff')};
  font-weight: ${({ active }) => (active ? 800 : 500)};
  font-style: ${({ active }) => (active ? 'Condensed ExtraBold' : 'Medium')};
  font-size: 20px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
`;

const StyledButton = styled(CTA)<{ disabled?: boolean }>`
  background-color: ${({ disabled }) => (disabled ? '#0C0C0C' : '#c90010')};
  color: ${({ disabled }) => (disabled ? '#7D7D7D' : '#ffffff')};
  border: ${({ disabled }) => (disabled ? '1px solid #7D7D7D' : 'none')};
  will-change: transform, background-color, color;
  transform-origin: center center;
  margin-left: auto;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  :hover,
  :active,
  :focus,
  :focus-visible {
    background: ${({ disabled }) => (disabled ? '#0C0C0C !important' : '#8a010cff !important')};
    color: ${({ disabled }) => (disabled ? '#7D7D7D !important' : '#ffffff !important')};
  }
`;

const CenteredContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
`;

export const QuestionsQuiz = (): React.ReactElement => {
  const { 'breadcrumb-service': breacrumbService, 'page-info-service': pageInfoService } = useFeatureServices();
  const [currentStep, setCurrentStep] = useState(1);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const confettiContainerRef = useRef<HTMLDivElement>(null);

  const createConfetti = () => {
    if (!confettiContainerRef.current) return;

    const colors = ['#c90010', '#ffffff'];
    const confettiCount = 220;

    // Distintos puntos de lanzado en la izquierda
    for (let i = 0; i < confettiCount / 2; i++) {
      const confetti = document.createElement('div');
      confetti.style.position = 'absolute';

      // Randomizamos la forma del confeti
      const size = 4 + Math.random() * 5;
      const isRectangle = Math.random() > 0.5;
      confetti.style.width = isRectangle ? size + 'px' : size * 0.7 + 'px';
      confetti.style.height = isRectangle ? size * 0.7 + 'px' : size + 'px';
      confetti.style.borderRadius = Math.random() > 0.7 ? '50%' : '2px';

      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.pointerEvents = 'none';
      confetti.style.zIndex = '9999';

      // Ditintos puntos de lanzado a lo largo de la parte izquierda
      confetti.style.left = '0px';
      confetti.style.top = 10 + Math.random() * 80 + '%';

      confettiContainerRef.current.appendChild(confetti);

      const tl = gsap.timeline({ onComplete: () => confetti.remove() });
      const isMobile = window.innerWidth <= 960;
      const xDistance = isMobile ? 200 + Math.random() * 250 : 400 + Math.random() * 500;

      tl.to(
        confetti,
        {
          x: xDistance,
          duration: 1.5,
          ease: 'none',
        },
        0,
      )
        .to(
          confetti,
          {
            y: -150 - Math.random() * 100,
            duration: 0.6,
            ease: 'power2.out',
          },
          0,
        )
        .to(
          confetti,
          {
            y: window.innerHeight + 100,
            duration: 0.9,
            ease: 'power2.in',
          },
          0.6,
        )
        .to(
          confetti,
          {
            rotation: Math.random() * 1080,
            duration: 1.5,
            ease: 'none',
          },
          0,
        );
    }

    // Distintos puntos de lanzado en la parte derecha
    for (let i = 0; i < confettiCount / 2; i++) {
      const confetti = document.createElement('div');
      confetti.style.position = 'absolute';

      // Randomizamos la forma del confeti
      const size = 4 + Math.random() * 5;
      const isRectangle = Math.random() > 0.5;
      confetti.style.width = isRectangle ? size + 'px' : size * 0.7 + 'px';
      confetti.style.height = isRectangle ? size * 0.7 + 'px' : size + 'px';
      confetti.style.borderRadius = Math.random() > 0.7 ? '50%' : '2px';

      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.pointerEvents = 'none';
      confetti.style.zIndex = '9999';

      // Distintos puntos de lanzado a lo largo de la parte derecha
      confetti.style.right = '0px';
      confetti.style.top = 10 + Math.random() * 80 + '%';

      confettiContainerRef.current.appendChild(confetti);

      const tl = gsap.timeline({ onComplete: () => confetti.remove() });
      const isMobile = window.innerWidth <= 960;
      const xDistance = isMobile ? -(200 + Math.random() * 250) : -(400 + Math.random() * 500);

      tl.to(
        confetti,
        {
          x: xDistance,
          duration: 1.5,
          ease: 'none',
        },
        0,
      )
        .to(
          confetti,
          {
            y: -150 - Math.random() * 100,
            duration: 0.6,
            ease: 'power2.out',
          },
          0,
        )
        .to(
          confetti,
          {
            y: window.innerHeight + 100,
            duration: 0.9,
            ease: 'power2.in',
          },
          0.6,
        )
        .to(
          confetti,
          {
            rotation: Math.random() * 1080,
            duration: 1.5,
            ease: 'none',
          },
          0,
        );
    }
  };

  const handleAnswerSelected = (isCorrect: boolean) => {
    if (isCorrect) {
      setIsButtonEnabled(true);
      createConfetti();
    }
  };

  const handleIncorrect = () => {
    setErrorCount((prev) => prev + 1);
  };

  const handleNext = () => {
    if (isButtonEnabled) {
      if (currentStep < 3) {
        const nextStep = currentStep + 1;

        gsap.fromTo(
          stepRefs.current[nextStep - 1],
          { scale: 1 },
          { scale: 1.2, duration: 0.2, yoyo: true, repeat: 1, ease: 'power2.out' },
        );

        setCurrentStep(nextStep);
        setIsButtonEnabled(false);
      } else {
        setShowForm(true);
      }
    }
  };

  React.useEffect(() => {
    breacrumbService.set([]);
    pageInfoService.setPageInfo();
  }, [pageInfoService, breacrumbService]);

  return (
    <>
      <BackgroundGtiColor>
        {showForm ? (
          <QuizFormLoader onClose={() => setShowForm(false)} errorCount={errorCount.toString()} />
        ) : (
          <>
            <CenteredContent>
              {currentStep === 1 && (
                <Question1 onAnswerSelected={handleAnswerSelected} handleIncorrect={handleIncorrect} />
              )}
              {currentStep === 2 && (
                <Question2 onAnswerSelected={handleAnswerSelected} handleIncorrect={handleIncorrect} />
              )}
              {currentStep === 3 && (
                <Question3 onAnswerSelected={handleAnswerSelected} handleIncorrect={handleIncorrect} />
              )}
            </CenteredContent>

            <BottomContainer>
              <StepsContainer>
                {[1, 2, 3].map((step) => (
                  <StepNumber
                    key={step}
                    ref={(element) => {
                      stepRefs.current[step - 1] = element;
                    }}
                    active={step === currentStep}
                  >
                    {step}
                  </StepNumber>
                ))}
              </StepsContainer>
              <StyledButton
                tag="button"
                emphasis="primary"
                onClick={handleNext}
                aria-label="aria label for CTA"
                type="button"
                disabled={!isButtonEnabled}
              >
                <Text appearance={TokenTextAppearance.headline100} bold>
                  Siguiente
                </Text>
              </StyledButton>
            </BottomContainer>
          </>
        )}
      </BackgroundGtiColor>
      <div
        ref={confettiContainerRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 9999,
        }}
      />
    </>
  );
};
